import { useEffect, useMemo, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { FileIcon, Trash2 } from "lucide-react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";
import { ClipLoader } from "react-spinners";
// import BulkEnrollGrid from "../components/bulkEnrollGrid";

const SUPPORTED_EXTENSIONS = [".csv", ".xlsx", ".xls"];

const parseCsvFile = (file) =>
    new Promise((resolve, reject) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            delimiter: ",",
            transformHeader: (header) => header.trim().toLowerCase(),
            complete: ({ data }) => resolve(data),
            error: reject,
        });
    });

const parseSpreadsheet = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
            const workbook = XLSX.read(new Uint8Array(reader.result), { type: "array" });
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            resolve(XLSX.utils.sheet_to_json(worksheet));
        };
        reader.readAsArrayBuffer(file);
    });

const parseFile = (file) =>
    file.name.toLowerCase().endsWith(".csv") ? parseCsvFile(file) : parseSpreadsheet(file);

const normaliseEmail = (row = {}) =>
    (row.email ?? row.Email ?? row.emails ?? row.Emails ?? row["e-mail"] ?? row["Email Address"] ?? "")
        .toString()
        .trim()
        .toLowerCase();

const clipboardTextToRows = (text) => {
    const cleaned = (text ?? "").replace(/\r/g, "").trim();
    if (!cleaned) return [];

    const lines = cleaned.split("\n").filter((l) => l.trim() !== "");
    if (!lines.length) return [];

    const delimiter = lines[0].includes("\t") ? "\t" : ",";
    const cells = lines.map((l) => l.split(delimiter).map((c) => c.trim()));

    // Detect header row
    const header = cells[0].map((h) => h.toLowerCase());
    const hasHeader = header.includes("email") || header.includes("e-mail");

    const findIdx = (names, fallback) => {
        for (const n of names) {
            const i = header.indexOf(n);
            if (i >= 0) return i;
        }
        return fallback;
    };

    const idx = hasHeader
        ? {
            email: findIdx(["email", "e-mail", "email address"], 0),
            cohort: findIdx(["cohort"], 1),
            slug: findIdx(["slug", "course", "course slug"], 2),
            tm: findIdx(["tm", "technical mentor", "mentor"], 3),
        }
        : { email: 0, cohort: 1, slug: 2, tm: 3 };

    const start = hasHeader ? 1 : 0;

    return cells.slice(start).map((row) => ({
        __id: crypto.randomUUID(),
        email: (row[idx.email] ?? "").toString().trim().toLowerCase(),
        cohort: (row[idx.cohort] ?? "").toString().trim(),
        slug: (row[idx.slug] ?? "").toString().trim(),
        tm: (row[idx.tm] ?? "").toString().trim(),
    }));
};

export default function CohortBulkEnrollDialog({
    open,
    onOpenChange,
    cohorts = [],
    onUploaded,
    templateUrl = `https://lms-internal.zinduaschool.com/templates/cohort-bulk-enroll-emails.csv`,
}) {
    // Removed select dropdown state for cohort, course, and TM
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [allStudents, setAllStudents] = useState([]);
    const [allCourses, setAllCourses] = useState([]);
    const [allTms, setAllTms] = useState([]);
    const [cohortEnrolledIds, setCohortEnrolledIds] = useState([]); // Can be removed if not used elsewhere
    const fileInputRef = useRef(null);
    const { getToken } = useAuth();
    const [entryMode, setEntryMode] = useState("upload");
    const [gridRows, setGridRows] = useState([]);

    const safeCohorts = useMemo(
        () => (Array.isArray(cohorts) ? cohorts.filter((c) => c?.id && c?.name) : []),
        [cohorts]
    );
    const safeCourses = useMemo(
        () => (Array.isArray(allCourses) ? allCourses.filter((c) => c?.id && c?.slug) : []),
        [allCourses]
    );
    const safeTms = useMemo(
        () => (Array.isArray(allTms) ? allTms.filter((tm) => tm?.id) : []),
        [allTms]
    );

    const addGridRow = () => {
        setGridRows((prev) => [
            ...prev,
            { __id: crypto.randomUUID(), email: "", cohort: "", slug: "", tm: "" },
        ]);
    };

    const pasteGridRows = async () => {
        try {
            const text = await navigator.clipboard.readText();
            const newRows = clipboardTextToRows(text);

            if (!newRows.length) {
                toast.error("Clipboard is empty or not in a supported format.");
                return;
            }

            setGridRows((prev) => [...prev, ...newRows]);
            toast.success(`Pasted ${newRows.length} row(s).`);
        } catch (e) {
            console.error(e);
            toast.error("Could not read clipboard. Try using HTTPS/localhost or paste via file upload.");
        }
    };

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const token = await getToken();
                if (!token) throw new Error("No token");

                const reqConfig = {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                };

                    const [allStudents, allTms, coursesResponse] = await Promise.all([
                        fetchAllPaginatedItems({
                            url: `${api_endpoint}/user/users`,
                            token,
                            selectItems: (data) => data?.users ?? [],
                            mode: "skip",
                            pageSize: 200,
                            headers: reqConfig.headers,
                        }),
                        fetchAllPaginatedItems({
                            url: `${api_endpoint}/user/users/role/${"Instructor"}`,
                            token,
                            selectItems: (data) => data?.users ?? [],
                            mode: "skip",
                            pageSize: 200,
                            headers: reqConfig.headers,
                        }),
                        axios.get(`${api_endpoint}/course/`),
                    ]);
                    setAllStudents(allStudents ?? []);
                    setAllTms(allTms ?? []);
                    setAllCourses(coursesResponse.data?.courses ?? []);
            } catch (error) {
                console.error("Failed to prime bulk cohort dialog", error);
                toast.error("Failed to load prerequisites for bulk enrolment");
                setAllStudents([]);
                setAllTms([]);
                setAllCourses([]);
            }
        })();
    }, [open, getToken]);


    const addFiles = (incomingList) => {
        if (!incomingList?.length) return;
        setFiles((prev) => {
            const seen = new Set(prev.map((file) => `${file.name}-${file.size}`));
            const next = Array.from(incomingList).filter((file) => {
                const extension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
                if (!SUPPORTED_EXTENSIONS.includes(extension)) {
                    toast.error(`Unsupported file type: ${extension}. Use CSV, XLS, or XLSX.`);
                    return false;
                }
                const key = `${file.name}-${file.size}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
            return [...prev, ...next];
        });
    };

    const onFileChange = (event) => addFiles(event.target.files);

    const onDrop = (event) => {
        event.preventDefault();
        addFiles(event.dataTransfer.files);
    };

    const removeFile = (name, size) =>
        setFiles((prev) => prev.filter((file) => file.name !== name || file.size !== size));

    const resetState = () => {
        setFiles([]);
        setUploading(false);
        setCohortEnrolledIds([]);
        setEntryMode("upload");
        setGridRows([]);
    };


    const handleUpload = async () => {
        let parsedRows = [];

        if (entryMode === "upload") {
            if (!files.length) {
                toast.error("Upload at least one CSV or Excel file.");
                return;
            }
            parsedRows = (await Promise.all(files.map(parseFile))).flat();
        } else {
            if (!gridRows.length) {
                toast.error("Add at least one student row.");
                return;
            }
            parsedRows = gridRows;
        }


        setUploading(true);
        try {
            const token = await getToken();
            if (!token) throw new Error("Missing auth token");

            // Parse all files
            // const parsedRows = (await Promise.all(files.map(parseFile))).flat();

            if (!parsedRows.length) {
                toast.error("No data found.");
                return;
            }

            // Lookup maps
            const emailToId = new Map(
                allStudents.map((u) => [u.email?.trim().toLowerCase(), u.id])
            );
            const cohortNameToId = new Map(
                safeCohorts.map((c) => [c.name?.trim().toLowerCase(), c.id])
            );
            const courseSlugToId = new Map(
                safeCourses.map((c) => [c.slug?.trim().toLowerCase(), c.id])
            );
            const tmNameToId = new Map(
                safeTms.flatMap((tm) => {
                    const fullName = `${tm.first_name ?? ""} ${tm.last_name ?? ""}`
                        .trim()
                        .toLowerCase();
                    const entries = [];
                    if (fullName) entries.push([fullName, tm.id]);
                    if (tm.email) entries.push([tm.email.toLowerCase(), tm.id]);
                    return entries;
                })
            );

            // Step 1: Collect valid enrollments
            const validEnrollments = parsedRows
                .map(row => {
                    const email = normaliseEmail(row);
                    const cohortId = cohortNameToId.get((row.cohort ?? "").trim().toLowerCase());
                    const courseId = courseSlugToId.get((row.slug ?? "").trim().toLowerCase());
                    const tmId = tmNameToId.get((row.tm ?? "").trim().toLowerCase());
                    const userId = emailToId.get(email);
                    if (userId && cohortId && courseId && tmId) {
                        return { userId, cohortId, courseId, tmId };
                    }
                    return null;
                })
                .filter(Boolean);

            if (!validEnrollments.length) {
                toast.error("No valid rows found for enrollment. Check email/cohort/slug/tm values.");
                return;
            }

            // Step 2: Group enrollments by cohort/course/TM
            const groupedEnrollments = {};
            validEnrollments.forEach(({ userId, cohortId, courseId, tmId }) => {
                const key = `${cohortId}-${courseId}-${tmId}`;
                if (!groupedEnrollments[key]) {
                    groupedEnrollments[key] = {
                        cohort_id: cohortId,
                        course_id: courseId,
                        tm_id: tmId,
                        user_ids: [],
                    };
                }
                groupedEnrollments[key].user_ids.push(userId);
            });

            const payload = Object.values(groupedEnrollments);

            // Preview what will be sent
            console.log("Grouped Enrollment Payload:", payload);

            // Step 3: POST request
            await axios.post(`${api_endpoint}/cohort/batch-enroll`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            }
            );

            toast.success(`${validEnrollments.length} students enrolled successfully.`);
            resetState();
            onOpenChange?.(false);
            onUploaded?.();
        } catch (error) {
            console.error("Bulk enrollment failed", error);
            toast.error(error?.response?.data?.message ?? "Failed to enroll students. Try again.");
        } finally {
            setUploading(false);
        }
    };



    return (
        <Dialog
            open={open}
            onOpenChange={(value) => {
                onOpenChange?.(value);
                if (!value) resetState();
            }}
        >
            <DialogContent className="max-w-2xl w-[95vw] bg-(--surface-base) text-(--neutral-900)">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-heading">Bulk Enroll Students to Cohort</DialogTitle>
                    <DialogDescription className="text-sm text-(--neutral-600)">
                        Upload CSV file of students to bulk enroll. {' '}
                        <a
                            href={templateUrl}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-(--color-primary) underline"
                        >
                            Click to download template
                        </a>
                    </DialogDescription>
                </DialogHeader>

                <div className="flex gap-2 mt-4">
                    <Button
                        type="button"
                        variant={entryMode === "upload" ? "default" : "outline"}
                        onClick={() => setEntryMode("upload")}
                        className="flex-1"
                    >
                        Upload CSV / Excel
                    </Button>

                    <Button
                        type="button"
                        variant={entryMode === "spreadsheet" ? "default" : "outline"}
                        onClick={() => setEntryMode("spreadsheet")}
                        className="flex-1"
                    >
                        Use Spreadsheet
                    </Button>
                </div>


                <div className="space-y-4">
                    {/* Select dropdowns for cohort, course, and TM removed. Only upload/manual entry options remain. */}

                    {entryMode === "upload" && (
                        <>
                            <div
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={onDrop}
                                className="flex flex-col items-center justify-center gap-3 border border-dashed border-(--neutral-300) rounded-lg py-10 px-4 text-center bg-(--surface-base) text-(--neutral-600)"
                            >
                                <FileIcon className="w-10 h-10 text-(--neutral-400)" />
                                <p className="font-heading text-(--neutral-800)">
                                    Drag and drop or
                                    {" "}
                                    <button
                                        type="button"
                                        className="text-(--color-primary) underline"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        Choose file
                                    </button>
                                    {" "}
                                    to upload
                                </p>
                                <Input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv,.xls,.xlsx"
                                    multiple
                                    className="hidden"
                                    onChange={onFileChange}
                                />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs text-(--neutral-500)">
                                <p className="text-sm">Accepted file types: CSV, XLS, XLSX</p>
                                <p className="text-sm">Max file size: 10MB</p>
                            </div>

                            {files.length > 0 && (
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {files.map((file) => (
                                        <div
                                            key={`${file.name}-${file.size}`}
                                            className="flex items-center justify-between border border-(--neutral-300) rounded-md px-3 py-2 bg-(--neutral-100)"
                                        >
                                            <div className="flex items-center gap-3">
                                                <FileIcon className="w-5 h-5 text-(--color-primary)" />
                                                <div>
                                                    <p className="text-sm font-medium">{file.name}</p>
                                                    <p className="text-xs text-(--neutral-500)">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="text-(--neutral-500) hover:text-(--color-destructive)"
                                                onClick={() => removeFile(file.name, file.size)}
                                                aria-label="Remove file"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {entryMode === "spreadsheet" && (
                        <div className="space-y-4">
                            
                                <p className="text-sm text-(--neutral-600)">
                                    Paste or type student data directly. You can paste rows from Excel.
                                </p>
                                
                                <div className="flex gap-2">
                                    <Button type="button" variant="outline" onClick={pasteGridRows} className="flex-1">
                                        Paste rows/columns
                                    </Button>
                                    <Button type="button" variant="outline" onClick={addGridRow} className="flex-1">
                                        Add row
                                    </Button>
                                </div>
                            

                            <BulkEnrollGrid
                                rows={gridRows}
                                setRows={setGridRows}
                                cohorts={safeCohorts}
                                courses={safeCourses}
                                tms={safeTms}
                                onRequestPaste={pasteGridRows}
                            />
                        </div>
                    )}

                </div>


                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                            onOpenChange?.(false);
                            resetState();
                        }}
                        disabled={uploading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        className=""
                        onClick={handleUpload}
                        disabled={
                            uploading ||
                            (entryMode === "upload" ? !files.length : !gridRows.length)
                        }
                    >
                        {uploading ? (
                            <span className="flex items-center gap-2">
                                <ClipLoader size={18} color="#fff" />
                            </span>
                        ) : (
                            "Confirm Upload"
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
